import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import { type Activity, type DirectLine } from 'botframework-directlinejs';
import { type Subscription } from 'rxjs';

import { AdsCopilotContext, type IAdsCopilotContextValue } from './ads-copilot-context';
import { isActivityRelevantToApp } from './is-activity-relevant-to-app';
import { findExistingActivityIndex } from '../conversation-thread/find-existing-activity-index';
import { type BotServiceConfig } from '../../types';
import { AdsCopilotDirectLine } from '../direct-line-provider/ads-copilot-direct-line';

export type AdsCopilotContextProviderProps = {
  children: React.ReactNode;
};

/**
 * App-level context for sharing Copilot chat information across the entire app.
 * Be aware that any changes here will have a perf impact to the entire app.
 */
export const AdsCopilotContextProvider: React.FC<AdsCopilotContextProviderProps> = React.memo((props) => {
  const { children } = props;
  const [, setDirectLine] = useState<DirectLine | undefined>();
  const [, setSubscription] = useState<Subscription | undefined>();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [botServiceConfig, setBotServiceConfig] = useState<BotServiceConfig | undefined>();
  const [pilotMap, onPilotMapChange] = useState<Record<string, boolean>>({});
  const [logger, setLogger] = useState<IAdsCopilotContextValue['logger']>();
  const [isSendDisabled, setIsSendDisabled] = useState(false);

  const onDirectLineCreated = useCallback<IAdsCopilotContextValue['onDirectLineCreated']>((dl, username, newLogger) => {
    setDirectLine(dl);
    setLogger(newLogger);

    const directLine = dl as AdsCopilotDirectLine;
    if (directLine.isCustomStream) {
      return;
    }

    setSubscription((prevSub) => {
      // unsubscribe from previous DirectLine
      prevSub?.unsubscribe();
      setActivities([]);

      // subscribe to all the complete messages received from DirectLine
      return dl.activity$.filter((activity) => isActivityRelevantToApp(dl, activity)).subscribe((activity) => {
        setActivities((prevActivities) => {
          const newActivities = [...prevActivities];

          // check if this is a streaming message that will replace a previous message
          const existingActivityIndex = findExistingActivityIndex(newActivities, activity, username);
          if (existingActivityIndex > -1) {
            newActivities[existingActivityIndex] = activity;
          } else {
            newActivities.push(activity);
          }

          return newActivities;
        });
      });
    });
  }, []);

  const value = useMemo<IAdsCopilotContextValue>(() => ({
    activities,
    botServiceConfig,
    logger,
    onBotServiceConfigChange: setBotServiceConfig,
    onDirectLineCreated,
    pilotMap,
    onPilotMapChange,
    onIsSendDisabledChange: setIsSendDisabled,
    isSendDisabled,
  }), [
    activities,
    botServiceConfig,
    logger,
    onDirectLineCreated,
    isSendDisabled,
  ]);

  return (
    <AdsCopilotContext.Provider value={value}>
      {children}
    </AdsCopilotContext.Provider>
  );
});
